import { useCallback, useEffect, useRef } from "react";
import { useCaptureCountdown } from "./useCaptureCountdown";

// EN-4743. Front photo of a driver's licence, state ID or "Others": once the camera
// is live, a 5 -> 1 countdown (the same one the passport and the back use) takes
// the photo, exactly as a tap on Capture would. Nothing is scanned and nothing is
// sent on its own: after the photo the visitor carries on with Retake / Continue
// as before. Capture stays available; tapping it just stops the countdown.
interface UseFrontAutoCaptureOptions {
  enabled: boolean;
  // Takes the photo, the same function the Capture button calls.
  takePhoto: () => void;
}

interface UseFrontAutoCaptureReturn {
  secondsLeft: number;
  running: boolean;
  // Call when the front camera starts streaming.
  onCameraLive: () => void;
  // Call before anything else takes the photo or closes the camera.
  stop: () => void;
}

export const useFrontAutoCapture = ({ enabled, takePhoto }: UseFrontAutoCaptureOptions): UseFrontAutoCaptureReturn => {
  const takePhotoRef = useRef(takePhoto);
  useEffect(() => {
    takePhotoRef.current = takePhoto;
  }, [takePhoto]);

  const onElapsed = useCallback(() => takePhotoRef.current(), []);
  // No isGlare: the front isn't checked for glare, so the countdown always ends on time.
  const { secondsLeft, running, start, stop } = useCaptureCountdown({ onElapsed });

  const onCameraLive = useCallback(() => {
    if (enabled) start();
  }, [enabled, start]);

  // Leaving the page: useCaptureCountdown clears its timer on unmount, so no photo
  // is ever taken of a camera that's no longer on screen.
  return { secondsLeft, running: enabled && running, onCameraLive, stop };
};
